import * as React from "react";
import {
  Card as MuiCard,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import {
  Notifications,
  NotificationsOff,
  NotificationsActive,
  NotificationsPaused,
} from "@material-ui/icons";
import appLogo from "../../../src/assets/logo.png";
import backIcon from "../../../src/assets/icons/backIcon.svg";
import Unsubscribe from "../Icons/UnSubscribe";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { leaveChannel } from "../../redux/actions/programAction";
import {
  subscribeToChannel,
  muteChannel,
  unmuteChannel,
  fetchChannelSubscriptions,
  fetchChannelPolicy,
  fetchCommunityPolicy,
} from "../../redux/actions/notificationSettingsActions";
import { assetUrl } from "../../util/assetUrl";
import { usePrivileges } from "../../util/usePrivileges";

/* ===== API/Asset bases ===== */
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;

/* ===== Resolve stored/relative/absolute paths to usable URLs ===== */
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

// helper to invert a hex color for readable text
function invertColor(hex: string) {
  let c = hex.replace(/^#/, "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const num = parseInt(c, 16);
  const r = 255 - (num >> 16);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// helper to determine if a color is light or dark
function isLightColor(hex: string) {
  const c = hex.replace(/^#/, "");
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
}

function ChannelSettings() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [subscribed, setSubscribed] = React.useState(true);
  const [currentSubscription, setCurrentSubscription] = React.useState<any>(null);
  const [channelPolicy, setChannelPolicy] = React.useState<any>(null);
  const [communityPolicy, setCommunityPolicy] = React.useState<any>(null);

  const { channelDetails, subscribeChannels, singleProgram } = useSelector(
    (state: RootState) => state.programReducer
  );

  const {
    channelSubscriptions,
    channelSubscriptionsLoading,
    subscribeToChannelLoading,
    unsubscribeFromChannelLoading,
    muteChannelLoading,
    unmuteChannelLoading,
  } = useSelector((state: RootState) => state.notificationSettingsReducer);

  const isLoading =
    channelSubscriptionsLoading ||
    subscribeToChannelLoading ||
    unsubscribeFromChannelLoading ||
    muteChannelLoading ||
    unmuteChannelLoading;

  // THEME (from channel or defaults)
  const {
    mainColor,
    secondaryColor,
    headerImage,
    fontStyle,
  } = (channelDetails as any)?.channelStyle || {
    mainColor: "#1976d2",
    secondaryColor: "#ffffff",
    headerImage: "",
    fontStyle: "Arial",
  };

  const invertedSecondary = React.useMemo(
    () => invertColor(secondaryColor),
    [secondaryColor]
  );

  /* ========= Get community and channel identifiers ========= */
  const activeProgramEnrollment = React.useMemo(
    () => JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}"),
    []
  );

  const communityIdentifier =
    (channelDetails as any)?.communityIdentifier ||
    activeProgramEnrollment?.communityIdentifier ||
    "";

  const channelIdentifier = (channelDetails as any)?.channelIdentifier || "";

  const { privileges } = usePrivileges(communityIdentifier || undefined);

  /* ========= Community logo ========= */
  const activeCommunity = React.useMemo(() => {
    if (!singleProgram || !communityIdentifier) return null;
    return (
      singleProgram.communities?.find(
        (c: any) => c?.communityIdentifier === communityIdentifier
      ) || null
    );
  }, [singleProgram, communityIdentifier]);

  const communityLogoRaw = activeCommunity?.communityStyle?.logo || "";
  const [logoBust, setLogoBust] = React.useState(0);
  React.useEffect(() => {
    setLogoBust(Date.now());
  }, [communityLogoRaw, communityIdentifier, singleProgram?._id]);

  const communityLogoUrl = React.useMemo(() => {
    const u = resolveHeaderUrl(communityLogoRaw);
    if (!u) return "";
    return `${u}${u.includes("?") ? "&" : "?"}v=${logoBust}`;
  }, [communityLogoRaw, logoBust]);

  /* ========= Fetch notification subscriptions and policies ========= */
  React.useEffect(() => {
    if (communityIdentifier) {
      dispatch(fetchChannelSubscriptions(communityIdentifier) as any);

      // Fetch community policy
      dispatch(fetchCommunityPolicy(communityIdentifier) as any)
        .then((res: any) => {
          if (res?.data) {
            setCommunityPolicy(res.data);
          }
        })
        .catch(() => {
          // Policy might not exist, that's okay
          setCommunityPolicy(null);
        });
    }

    if (communityIdentifier && channelIdentifier) {
      // Fetch channel-specific policy
      dispatch(fetchChannelPolicy(communityIdentifier, channelIdentifier) as any)
        .then((res: any) => {
          if (res?.data) {
            setChannelPolicy(res.data);
          }
        })
        .catch(() => {
          // Policy might not exist, that's okay
          setChannelPolicy(null);
        });
    }
  }, [dispatch, communityIdentifier, channelIdentifier]);

  /* ========= Update current subscription when list changes ========= */
  React.useEffect(() => {
    const subscription = channelSubscriptions.find(
      (sub) =>
        sub.communityIdentifier === communityIdentifier &&
        sub.channelIdentifier === channelIdentifier
    );
    setCurrentSubscription(subscription || null);
  }, [channelSubscriptions, communityIdentifier, channelIdentifier]);

  /* ========= Subscribe state (for channel membership) ========= */
  React.useEffect(() => {
    const isSubscribed = subscribeChannels?.some(
      (item) =>
        item.channelIdentifier &&
        (item.channelIdentifier === channelDetails?.channelIdentifier ||
          item.channelIdentifier === (channelDetails as any)?.label)
    );
    setSubscribed(!!isSubscribed);
  }, [channelDetails, subscribeChannels]);

  const canViewChannel = React.useMemo(() => {
    const channelId = (channelDetails as any)?.channelIdentifier;
    if (!channelId || !communityIdentifier) return false;
    return privileges.canViewChannel(communityIdentifier, channelId);
  }, [privileges, communityIdentifier, channelDetails]);

  /* ========= Handlers ========= */
  const handleFrequencyChange = (frequency: 'immediate' | 'daily' | 'off') => {
    dispatch(
      subscribeToChannel(communityIdentifier, channelIdentifier, {
        frequency,
        emailEnabled: true,
        pushEnabled: false,
      }) as any
    );
  };

  const handleMute = () => {
    dispatch(muteChannel(communityIdentifier, channelIdentifier, 24) as any);
  };

  const handleUnmute = () => {
    dispatch(unmuteChannel(communityIdentifier, channelIdentifier) as any);
  };

  const unSubscribeChannel = (ch: any) => {
    const activeProgramEnrollment = JSON.parse(
      localStorage.getItem("activeProgramEnrollment") || "{}"
    );
    const auth = JSON.parse(localStorage.getItem("authInfo") || "{}");
    const channelIdentifier = ch?.channelIdentifier;
    dispatch(
      leaveChannel(
        activeProgramEnrollment?.programId,
        auth?._id,
        activeProgramEnrollment?.communityIdentifier,
        channelIdentifier
      )
    );
    history.push("/user/dashboard");
  };

  const isMuted = currentSubscription?.mutedUntil && new Date(currentSubscription.mutedUntil) > new Date();

  // Determine effective frequency with correct precedence
  const getEffectiveFrequency = (): 'immediate' | 'daily' | 'off' => {
    // 1. If channel policy is ENFORCED → always use it (admin override)
    if (channelPolicy?.policyMode === 'enforced') {
      return channelPolicy?.defaults?.frequency || 'daily';
    }

    // 2. If community policy is ENFORCED → always use it (admin override)
    if (communityPolicy?.policyMode === 'enforced') {
      return communityPolicy?.defaults?.globalFrequency || 'daily';
    }

    // 3. If user has a subscription → use user's preference
    if (currentSubscription?.frequency) {
      return currentSubscription.frequency;
    }

    // 4. If channel policy is RECOMMENDED → use as default
    if (channelPolicy?.defaults?.frequency) {
      return channelPolicy.defaults.frequency;
    }

    // 5. If community policy is RECOMMENDED → use as default
    if (communityPolicy?.defaults?.globalFrequency) {
      return communityPolicy.defaults.globalFrequency;
    }

    // 6. Fallback to 'daily'
    return 'daily';
  };

  const effectiveFrequency = getEffectiveFrequency();
  const currentFrequency = currentSubscription?.frequency || effectiveFrequency;

  // Check if admin policies will override user settings
  const hasChannelOverride = channelPolicy?.policyMode === 'enforced';
  const hasCommunityOverride = !hasChannelOverride && communityPolicy?.policyMode === 'enforced';
  const hasAnyOverride = hasChannelOverride || hasCommunityOverride;

  // Get notification icon based on current state
  const getNotificationIcon = () => {
    if (isLoading) {
      return <CircularProgress size={24} style={{ color: mainColor }} />;
    }
    if (isMuted) {
      return <NotificationsPaused style={{ color: mainColor, fontSize: 32 }} />;
    }
    if (!currentSubscription && effectiveFrequency === 'off') {
      return <NotificationsOff style={{ color: invertedSecondary + '88', fontSize: 32 }} />;
    }
    if (effectiveFrequency === 'immediate') {
      return <NotificationsActive style={{ color: mainColor, fontSize: 32 }} />;
    }
    if (effectiveFrequency === 'daily') {
      return <Notifications style={{ color: mainColor, fontSize: 32 }} />;
    }
    return <NotificationsOff style={{ color: invertedSecondary + '88', fontSize: 32 }} />;
  };

  const getNotificationStatusText = () => {
    if (isMuted) {
      return `Muted until ${new Date(currentSubscription.mutedUntil).toLocaleString()}`;
    }
    if (!currentSubscription && !hasAnyOverride) {
      return `Default: ${effectiveFrequency} notifications (from ${channelPolicy?.defaults?.frequency ? 'channel' : 'community'} policy)`;
    }
    if (!currentSubscription && hasAnyOverride) {
      return `Enforced: ${effectiveFrequency} notifications (admin policy active)`;
    }
    if (hasAnyOverride && currentFrequency !== effectiveFrequency) {
      return `Your preference (${currentFrequency}) is overridden by admin policy (${effectiveFrequency})`;
    }
    if (effectiveFrequency === 'immediate') {
      return "Immediate notifications enabled";
    }
    if (effectiveFrequency === 'daily') {
      return "Daily digest notifications enabled";
    }
    return "Notifications disabled";
  };

  // Styled containers
  const containerStyle: React.CSSProperties = {
    backgroundColor: secondaryColor,
    color: invertedSecondary,
    fontFamily: fontStyle,
    minHeight: "100vh",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: secondaryColor,
    border: `1px solid ${invertedSecondary}22`,
    boxShadow: `0 4px 20px ${invertedSecondary}15`,
    borderRadius: "12px",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    position: "relative",
    height: "120px",
    background: headerImage
      ? `linear-gradient(135deg, ${mainColor}88, ${mainColor}44), url("${headerImage}")`
      : `linear-gradient(135deg, ${mainColor}, ${
          secondaryColor === "#ffffff" ? "#e3f2fd" : mainColor
        }dd)`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    padding: "20px",
  };

  const headerOverlayStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))`,
  };

  const backButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: "20px",
    left: "20px",
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    color: "white",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: 700,
    marginBottom: "15px",
    color: mainColor,
    padding: "25px 25px 0 25px",
  };

  const unsubscribeButtonStyle: React.CSSProperties = {
    backgroundColor: "transparent",
    color: `${invertedSecondary}88`,
    border: `1px solid ${invertedSecondary}33`,
    borderRadius: "8px",
    padding: "12px 24px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "none",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  };

  if (!canViewChannel) {
    return (
      <div style={containerStyle}>
        <Box p={4}>
          <Typography style={{ color: invertedSecondary }}>
            You no longer have access to this channel.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <MuiCard style={cardStyle}>
        {/* Enhanced Header */}
        <div style={headerStyle}>
          <div style={headerOverlayStyle}></div>
          <Link style={backButtonStyle} to="/user/channel">
            <img
              style={{
                width: 20,
                height: 20,
                objectFit: "contain",
                flexShrink: 0,
                marginRight: 8,
                filter: "brightness(0) invert(1)",
              }}
              src={backIcon}
              alt="Back"
            />
            <img
              style={{
                width: 72,
                height: 24,
                flexShrink: 0,
              }}
              src={communityLogoUrl || resolveHeaderUrl(appLogo)}
              alt="Logo"
            />
          </Link>
        </div>

        <Typography style={titleStyle}>Channel Settings</Typography>

        <div style={{ padding: "0 25px 25px 25px" }}>
          {/* Current Notification Status */}
          <Box
            style={{
              backgroundColor: `${invertedSecondary}08`,
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "25px",
              border: `1px solid ${invertedSecondary}15`,
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                marginBottom: "15px",
              }}
            >
              {getNotificationIcon()}
              <div>
                <Typography
                  style={{
                    color: invertedSecondary,
                    fontSize: "18px",
                    fontWeight: 600,
                    marginBottom: "5px",
                  }}
                >
                  Notification Status
                </Typography>
                <Typography
                  style={{
                    color: `${invertedSecondary}88`,
                    fontSize: "14px",
                  }}
                >
                  {getNotificationStatusText()}
                </Typography>
              </div>
            </Box>

            <Divider style={{ margin: "15px 0" }} />

            {/* Quick Actions */}
            <Typography
              style={{
                color: invertedSecondary,
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "12px",
              }}
            >
              Quick Actions
            </Typography>

            <Box style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Frequency Buttons */}
              <Box style={{ display: "flex", gap: "10px" }}>
                <Button
                  variant={effectiveFrequency === 'immediate' && !isMuted ? "contained" : "outlined"}
                  onClick={() => handleFrequencyChange('immediate')}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    backgroundColor: effectiveFrequency === 'immediate' && !isMuted ? mainColor : 'transparent',
                    color: effectiveFrequency === 'immediate' && !isMuted
                      ? (isLightColor(mainColor) ? "#000" : "#fff")
                      : invertedSecondary,
                    borderColor: effectiveFrequency === 'immediate' && hasAnyOverride ? "#ff9800" : `${invertedSecondary}33`,
                    borderWidth: effectiveFrequency === 'immediate' && hasAnyOverride ? "2px" : "1px",
                    textTransform: "none",
                    fontWeight: 600,
                    position: "relative",
                  }}
                >
                  Immediate
                  {effectiveFrequency === 'immediate' && hasAnyOverride && (
                    <span style={{ position: "absolute", top: "-8px", right: "-8px", fontSize: "16px" }}>⚠️</span>
                  )}
                </Button>
                <Button
                  variant={effectiveFrequency === 'daily' && !isMuted ? "contained" : "outlined"}
                  onClick={() => handleFrequencyChange('daily')}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    backgroundColor: effectiveFrequency === 'daily' && !isMuted ? mainColor : 'transparent',
                    color: effectiveFrequency === 'daily' && !isMuted
                      ? (isLightColor(mainColor) ? "#000" : "#fff")
                      : invertedSecondary,
                    borderColor: effectiveFrequency === 'daily' && hasAnyOverride ? "#ff9800" : `${invertedSecondary}33`,
                    borderWidth: effectiveFrequency === 'daily' && hasAnyOverride ? "2px" : "1px",
                    textTransform: "none",
                    fontWeight: 600,
                    position: "relative",
                  }}
                >
                  Daily
                  {effectiveFrequency === 'daily' && hasAnyOverride && (
                    <span style={{ position: "absolute", top: "-8px", right: "-8px", fontSize: "16px" }}>⚠️</span>
                  )}
                </Button>
                <Button
                  variant={effectiveFrequency === 'off' ? "contained" : "outlined"}
                  onClick={() => handleFrequencyChange('off')}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    backgroundColor: effectiveFrequency === 'off' ? mainColor : 'transparent',
                    color: effectiveFrequency === 'off'
                      ? (isLightColor(mainColor) ? "#000" : "#fff")
                      : invertedSecondary,
                    borderColor: effectiveFrequency === 'off' && hasAnyOverride ? "#ff9800" : `${invertedSecondary}33`,
                    borderWidth: effectiveFrequency === 'off' && hasAnyOverride ? "2px" : "1px",
                    textTransform: "none",
                    fontWeight: 600,
                    position: "relative",
                  }}
                >
                  Off
                  {effectiveFrequency === 'off' && hasAnyOverride && (
                    <span style={{ position: "absolute", top: "-8px", right: "-8px", fontSize: "16px" }}>⚠️</span>
                  )}
                </Button>
              </Box>

              {/* Mute/Unmute Button */}
              {currentSubscription && (
                <Button
                  variant="outlined"
                  onClick={isMuted ? handleUnmute : handleMute}
                  disabled={isLoading}
                  style={{
                    width: "100%",
                    color: invertedSecondary,
                    borderColor: `${invertedSecondary}33`,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {isMuted ? "Unmute Channel" : "Mute for 24 Hours"}
                </Button>
              )}
            </Box>

            {/* Admin Override Warning */}
            {hasAnyOverride && (
              <Box
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#ff9800",
                  borderRadius: "8px",
                  border: "2px solid #f57c00",
                }}
              >
                <Box style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <Typography style={{ fontSize: "20px" }}>⚠️</Typography>
                  <div>
                    <Typography
                      style={{
                        color: "#000",
                        fontSize: "14px",
                        fontWeight: 700,
                        marginBottom: "5px",
                      }}
                    >
                      Admin Policy Override Active
                    </Typography>
                    <Typography
                      style={{
                        color: "#333",
                        fontSize: "13px",
                        lineHeight: 1.5,
                      }}
                    >
                      {hasChannelOverride
                        ? `A channel administrator has enforced a notification policy for this channel. Your selection will be saved but notifications will follow the admin policy: ${effectiveFrequency.toUpperCase()}.`
                        : `A community administrator has enforced a notification policy for all channels. Your selection will be saved but notifications will follow the admin policy: ${effectiveFrequency.toUpperCase()}.`}
                    </Typography>
                  </div>
                </Box>
              </Box>
            )}

            {/* Regular Note */}
            <Box
              style={{
                marginTop: "15px",
                padding: "12px",
                backgroundColor: `${mainColor}15`,
                borderRadius: "8px",
              }}
            >
              <Typography
                style={{
                  color: invertedSecondary,
                  fontSize: "13px",
                  lineHeight: 1.5,
                }}
              >
                <strong>Note:</strong> These settings control notifications for this channel only.
                {!hasAnyOverride && " Admin policies may override your preferences."}
              </Typography>
            </Box>
          </Box>

          {/* Unsubscribe Section */}
          {subscribed && (
            <div style={{ marginTop: "30px" }}>
              <Typography
                style={{
                  color: invertedSecondary,
                  fontSize: "18px",
                  fontWeight: 600,
                  marginBottom: "10px",
                }}
              >
                Channel Membership
              </Typography>
              <Button
                variant="outlined"
                onClick={() => unSubscribeChannel(channelDetails)}
                style={unsubscribeButtonStyle}
              >
                <Unsubscribe />
                <span>Unsubscribe from Channel</span>
              </Button>
              <Typography
                style={{
                  color: `${invertedSecondary}66`,
                  fontSize: "12px",
                  marginTop: "8px",
                  textAlign: "center",
                }}
              >
                You will no longer see posts from this channel
              </Typography>
            </div>
          )}
        </div>
      </MuiCard>
    </div>
  );
}

export default ChannelSettings;
